import { useEffect, useRef, useState } from 'react'
import { FiEdit, FiMenu, FiMoreHorizontal, FiPhoneOff, FiVideo } from 'react-icons/fi'
import { TbEdit } from 'react-icons/tb'
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'

import useTutorCallDesktop from '@/hooks/useTutorCallDesktop'
import { useChatStore, useTutorCallStore, useUserStore } from '@/store'

import RenameModal from './RenameModal'

async function withTutorController(run: (c: typeof import('@/tutorCall/tutorCallController').tutorCallController) => void | Promise<void>) {
  const { tutorCallController } = await import('@/tutorCall/tutorCallController')
  await run(tutorCallController)
}

interface ChatHeaderProps {
  showTitle?: boolean
  showResourcePreview?: boolean
}

export default function ChatHeader({ showTitle = true, showResourcePreview: _showResourcePreview = true }: ChatHeaderProps) {
  const tutorDesktop = useTutorCallDesktop()
  const navigate = useNavigate()
  const { sessionId } = useParams<{ sessionId?: string }>()
  const { isSidebarOpen, setIsSidebarOpen, setIsLoginModalOpen, isResourcePreviewOpen, sessionList, triggerNewChat } = useChatStore()
  const currentSessionTitle = useChatStore((state) => {
    if (!sessionId) return '新对话'
    const localTitle = state.sessionTitleMap[sessionId]?.trim()
    if (localTitle) return localTitle
    const session = state.sessionList.find((item) => item.sessionId === sessionId)
    return session?.title?.trim() || '新对话'
  })
  const user = useUserStore((state) => state.user)
  const persistedRole = useTutorCallStore((s) => s.persistedRole)
  const pickedRole = useTutorCallStore((s) => s.pickedRole)
  const pickRole = useTutorCallStore((s) => s.pickRole)
  const phase = useTutorCallStore((s) => s.phase)
  const callFlowLoading = useTutorCallStore((s) => s.callFlowLoading)
  const role = pickedRole ?? persistedRole
  const needPickRole = Boolean(tutorDesktop && user && role == null)

  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [renameInitialTitle, setRenameInitialTitle] = useState('')
  const moreRef = useRef<HTMLDivElement>(null)
  const currentSession = sessionId ? sessionList.find((item) => item.sessionId === sessionId) : undefined
  const canRenameCurrentSession = !!sessionId && !currentSession?.defaultSession

  useEffect(() => {
    useTutorCallStore.getState().syncUser(user ?? null)
  }, [user])

  const showEndCallButton = tutorDesktop && Boolean(user) && !needPickRole && ((role === 'student' && (phase === 'connected' || phase === 'outgoing')) || (role === 'teacher' && phase === 'connected'))

  const endCallLabel = role === 'student' && phase === 'outgoing' ? '取消呼叫' : '结束通话'

  /** 窄屏依赖「更多」浮层：idle 时呼叫老师；呼出/通话中需露出「通话中」与挂断 */
  const showTutorMoreMenuTrigger = tutorDesktop && Boolean(user) && !needPickRole && ((role === 'student' && phase === 'idle') || showEndCallButton)

  const handleEndCall = () => {
    if (!tutorDesktop) return
    void withTutorController((c) => {
      if (role === 'student' && phase === 'outgoing') {
        void c.cancelOutgoing()
      } else {
        void c.hangup()
      }
    })
  }

  /** 无 ⋯ 入口时强制视为关闭，避免仅用 isMoreOpen 时浮层误显 */
  const isTutorMoreMenuOpen = showTutorMoreMenuTrigger && isMoreOpen

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setIsMoreOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <>
      {tutorDesktop && user && needPickRole && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">选择身份</h2>
            <p className="mt-2 text-sm text-gray-600">登录后请先选择当前使用身份，将用于音视频通话与信令（仅电脑端可用）。</p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                className="flex-1 rounded-xl border border-gray-200 bg-gray-50 py-3 text-sm font-medium text-gray-800 hover:bg-gray-100"
                onClick={() => user && pickRole(user, 'student')}
              >
                学员
              </button>
              <button type="button" className="flex-1 rounded-xl bg-emerald-600 py-3 text-sm font-medium text-white hover:bg-emerald-700" onClick={() => user && pickRole(user, 'teacher')}>
                老师
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-16 items-center justify-between p-4 pointer-events-none bg-white/80 backdrop-blur-sm border-b border-gray-100 z-20">
        {/* Left Actions */}
        <div className="flex items-center gap-1 pointer-events-auto">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
            title={isSidebarOpen ? '收起侧边栏' : '展开侧边栏'}
          >
            <FiMenu className="h-5 w-5 tablet:hidden" />
            <div className="hidden tablet:block">{isSidebarOpen ? <TbLayoutSidebarLeftCollapse className="h-5 w-5" /> : <TbLayoutSidebarLeftExpand className="h-5 w-5" />}</div>
          </button>
          <button
            className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
            title="新对话"
            onClick={() => {
              triggerNewChat()
              navigate('/chat')
            }}
          >
            <FiEdit className="h-4 w-4" />
          </button>
        </div>

        {/* Center Title (Only visible when in a conversation) */}
        {showTitle && (
          <div className="flex flex-col items-center pointer-events-auto absolute left-1/2 -translate-x-1/2 max-w-[40%]">
            <div className="flex w-full items-center justify-center gap-1">
              {canRenameCurrentSession ? (
                <button
                  onClick={() => {
                    setRenameInitialTitle(currentSessionTitle)
                    setIsRenameModalOpen(true)
                  }}
                  className="inline-flex h-6 px-2.5 py-2 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                  title="重命名"
                >
                  <div className="text-[14px] font-medium text-gray-800 truncate text-center">{currentSessionTitle}</div>
                  <TbEdit className="h-3.5 w-3.5 ml-1" />
                </button>
              ) : (
                <div className="inline-flex h-6 px-2.5 py-2 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors">
                  <div className="text-[14px] font-medium text-gray-800 truncate text-center">新对话</div>
                </div>
              )}
            </div>
            <span className="text-[10px] text-gray-400">内容由 AI 生成</span>
          </div>
        )}

        {/* Right Actions */}
        <div className="flex items-center gap-1 pointer-events-auto ">
          {tutorDesktop && user && phase === 'connected' && (
            <div
              className={`hidden ${!isResourcePreviewOpen ? 'desktop:flex' : ''} mr-1 items-center rounded-full border border-gray-200 bg-white/95 px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm`}
            >
              通话中
            </div>
          )}

          {tutorDesktop && user && role === 'student' && !needPickRole && phase === 'idle' && (
            <button
              type="button"
              disabled={callFlowLoading}
              className={`hidden ${!isResourcePreviewOpen ? 'desktop:flex' : ''} items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm font-medium disabled:cursor-wait disabled:opacity-75`}
              onClick={() => void withTutorController((c) => void c.openPickTeacher())}
            >
              {callFlowLoading ? <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden /> : <FiVideo className="h-4 w-4" />}
              <span>{callFlowLoading ? '加载中…' : '呼叫老师'}</span>
            </button>
          )}

          {showEndCallButton && (
            <button
              type="button"
              className={`hidden ${!isResourcePreviewOpen ? 'desktop:flex' : ''} items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-medium`}
              onClick={handleEndCall}
            >
              <FiPhoneOff className="h-4 w-4" />
              <span>{endCallLabel}</span>
            </button>
          )}
          {/* {showResourcePreview && (
            <button
              className={`hidden ${!isResourcePreviewOpen ? 'desktop:flex' : ''} items-center gap-1.5 px-3 py-1.5 rounded-full text-gray-600 hover:bg-gray-100 transition-colors text-sm font-medium`}
              onClick={() => setIsResourcePreviewOpen(!isResourcePreviewOpen)}
            >
              <FiLayout className="h-4 w-4" />
              <span>资源预览</span>
            </button>
          )} */}

          {/* 窄屏：⋯ 内展示「通话中」、挂断/取消呼叫（与 idle 时「呼叫老师」）；宽屏工具栏用 desktop:flex 单独展示 */}
          <div className={`relative ${!isResourcePreviewOpen ? 'desktop:hidden' : ''}`} ref={moreRef}>
            {showTutorMoreMenuTrigger && (
              <button
                type="button"
                onClick={() => {
                  setIsMoreOpen((o) => !o)
                }}
                className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${isTutorMoreMenuOpen ? 'bg-gray-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <FiMoreHorizontal className="h-5 w-5" />
              </button>
            )}

            {/* 更多菜单浮层 */}
            {tutorDesktop && isTutorMoreMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-36 rounded-lg bg-white py-2 shadow-lg ring-1 ring-black/5 z-50 flex flex-col">
                {/* <button className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                  <FiMic className="h-4 w-4 text-gray-500" />
                  <span>语音通话</span>
                </button> */}
                {user && phase === 'connected' && <div className="px-4 py-2 text-xs font-medium text-gray-700 border-b border-gray-50">通话中</div>}
                {user && role === 'student' && phase === 'outgoing' && <div className="px-4 py-2 text-xs font-medium text-gray-700 border-b border-gray-50">正在呼叫老师…</div>}
                {user && role === 'student' && !needPickRole && phase === 'idle' && (
                  <button
                    type="button"
                    disabled={callFlowLoading}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:cursor-wait disabled:opacity-60"
                    onClick={() => {
                      setIsMoreOpen(false)
                      void withTutorController((c) => void c.openPickTeacher())
                    }}
                  >
                    {callFlowLoading ? (
                      <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" aria-hidden />
                    ) : (
                      <FiVideo className="h-4 w-4 text-gray-500" />
                    )}
                    <span>{callFlowLoading ? '加载中…' : '呼叫老师'}</span>
                  </button>
                )}
                {showEndCallButton && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    onClick={() => {
                      setIsMoreOpen(false)
                      handleEndCall()
                    }}
                  >
                    <FiPhoneOff className="h-4 w-4" />
                    <span>{endCallLabel}</span>
                  </button>
                )}
                {/* <div className="mx-3 my-1 h-px bg-gray-100" />
                {showResourcePreview && (
                  <button
                    className={`flex w-full items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isResourcePreviewOpen ? 'text-blue-600 bg-blue-50/50' : 'text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => {
                      setIsResourcePreviewOpen(!isResourcePreviewOpen)
                      setIsMoreOpen(false)
                    }}
                  >
                    <FiLayout className={`h-4 w-4 ${isResourcePreviewOpen ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span>资源预览</span>
                  </button>
                )} */}
              </div>
            )}
          </div>

          {!user && !sessionId && (
            <button
              onClick={() => setIsLoginModalOpen(true)}
              className="rounded-full bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 transition-colors pointer-events-auto"
            >
              登录
            </button>
          )}
        </div>
      </div>
      <RenameModal
        isOpen={isRenameModalOpen}
        sessionId={sessionId ?? null}
        initialTitle={renameInitialTitle}
        onClose={() => {
          setIsRenameModalOpen(false)
        }}
      />
    </>
  )
}
